import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useState } from "react";

import { Badge, Modal, Button, Table } from "@mantine/core";

import TableSkeleton from "../../components/Skeleton/TableSkeleton";
import api from "../../services";
import { config } from "../../config";

import dayjs from "dayjs";

const SpotsCustomList = ({ data }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProducts, setModalProducts] = useState([]);
  const [selectedSaleSpot, setSelectedSaleSpot] = useState(null);
  const [loading, setLoading] = useState(false);

  const tempReturner = (status) => {
    if (status.includes("WORKING_TEMPERATURE")) {
      const temp = status.split("_")[status.split("_").length - 1];

      return temp + "°C";
    }
    return t("status.passive");
  };

  const getStatusBadgeColor = (status) => {
    if (status.includes("WORKING_TEMPERATURE")) {
      return "green";
    }
    switch (status) {
      case "WORKING_TEMPERATURE_13":
      case "WORKING_TEMPERATURE_6":
      case "WORKING_TEMPERATURE_3":
        return "green";
      case "DEACTIVE":
        return "red";
      default:
        return "gray";
    }
  };

  const getStockStatusBadgeColor = (status) => {
    switch (status) {
      case "FULL":
        return "green";
      case "NORMAL":
        return "blue";
      case "CRITICAL":
        return "red";
      default:
        return "gray";
    }
  };

  const handleRowClick = (saleSpotId) => {
    navigate(`/salespot/${saleSpotId}`);
  };

  const handleDetailsClick = async (saleSpot, event) => {
    event.stopPropagation();
    setSelectedSaleSpot(saleSpot);
    setLoading(true);
    setIsModalOpen(true);

    try {
      const response = await api.getSaleSpotItemProducts(
        saleSpot.showcase_ids[0]
      );
      setModalProducts(response.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      setModalProducts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 w-full md:pb-20">
      {/* Data Period Info */}
      <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-900/40 p-2 rounded-full">
              📅
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                Veri Rapor Dönemi
              </h3>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Aşağıdaki veriler bugüne kadar olan toplam değerleri
                göstermektedir
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {dayjs().format("DD MMMM YYYY")}
            </div>
            <div className="text-xs text-blue-700 dark:text-blue-300">
              Bugüne kadar
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flow-root w-full">
        <div className="-mx-4 -my-2 overflow-scroll sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className=" shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="dark:bg-gray-800 bg-gray-300 text-slate-700">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("labels.status")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.name")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("sales.sub-sales-area")}
                    </th>

                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.average-transaction-interval")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.success-rate")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.average-cart-value")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.total-sale-price")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.total-transaction-count")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.total-product-count")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.unique-customer-count")}
                    </th>

                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.temperature")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.stock-status")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.stock-percentage")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("salespot.current-capacity")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold dark:text-gray-300 text-slate-700 sm:pl-6"
                    >
                      {t("labels.details")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:bg-gray-800 bg-gray-200 text-slate-700">
                  {!data?.sale_spots ? (
                    <TableSkeleton colSize={8} />
                  ) : (
                    data.sale_spots.map((spot, index) => (
                      <tr
                        key={`${spot.sale_spot_id}-${index.toString()}`}
                        className={`${
                          index % 2 === 0
                            ? "dark:bg-slate-700/80 bg-gray-200"
                            : "dark:bg-slate-600/80 bg-gray-100"
                        } hover:bg-orange-200/90 dark:hover:bg-orange-600/20 cursor-pointer`}
                        onClick={() => handleRowClick(spot.sale_spot_id)}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <Badge
                            color={getStatusBadgeColor(spot.sale_spot_status)}
                            variant="filled"
                            size="sm"
                          >
                            {spot.sale_spot_status === "DEACTIVE"
                              ? t("status.passive")
                              : t("status.active")}
                          </Badge>
                        </td>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <span className="font-semibold">
                              {spot.sale_spot_name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {spot.sale_spot_location_description}
                            </span>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <span>{spot.sale_area_name}</span>
                            <span className="text-xs text-gray-500">
                              {spot.sale_area_slug}
                            </span>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <div>
                              {spot.showcase_stats.average_transaction_interval}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <div>{spot.showcase_stats.success_percentage}</div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <div>
                              {Intl.NumberFormat("tr-TR", {
                                style: "currency",
                                currency: "TRY",
                              }).format(spot.showcase_stats.average_cart_price)}
                            </div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <div>
                              {Intl.NumberFormat("tr-TR", {
                                style: "currency",
                                currency: "TRY",
                              }).format(spot.showcase_stats.total_price)}
                            </div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <div>
                              {spot.showcase_stats.total_transaction_count}
                            </div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <div>{spot.showcase_stats.total_product_count}</div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <div>{spot.showcase_stats.unique_user_count}</div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <div>{tempReturner(spot.sale_spot_status)}</div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <Badge
                            color={getStockStatusBadgeColor(
                              spot.stock_status.status
                            )}
                            variant="filled"
                            size="sm"
                          >
                            {t(
                              `salespot.stock-status-${spot.stock_status.status.toLowerCase()}`
                            )}
                          </Badge>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-3 h-3 rounded-full ${
                                spot.stock_status.percentage >= 70
                                  ? "bg-green-500"
                                  : spot.stock_status.percentage >= 30
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                              }`}
                            />
                            <span>
                              {parseFloat(spot.stock_status.percentage).toFixed(
                                0
                              )}
                              %
                            </span>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {spot.stock_status.total_current_stock} /{" "}
                              {spot.stock_status.total_capacity}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t("salespot.available-slots", {
                                count:
                                  spot.stock_status.total_capacity -
                                  spot.stock_status.total_current_stock,
                              })}
                            </span>
                          </div>
                        </td>

                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium dark:text-gray-300 text-slate-700 sm:pl-6">
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDetailsClick(spot, event);
                            }}
                          >
                            {t("labels.details")}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <div className="space-y-2">
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {selectedSaleSpot?.sale_spot_name || "Satış Noktası"}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-4">
              <span>📍 {selectedSaleSpot?.sale_spot_location_description}</span>
              <span>🏢 {selectedSaleSpot?.sale_area_name}</span>
              <span>
                🌡️ {tempReturner(selectedSaleSpot?.sale_spot_status || "")}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full inline-block">
              {t("labels.product-details")}
            </div>
          </div>
        }
        size="xl"
        classNames={{
          content: "dark:!bg-gray-800 dark:!border-gray-700",
          header: "dark:!bg-gray-800 dark:!border-gray-700",
          body: "dark:!bg-gray-800",
          close: "dark:!text-gray-100 dark:hover:!bg-gray-700",
        }}
      >
        <div className="dark:bg-gray-800 dark:text-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 dark:border-blue-400"></div>
              <div className="text-lg font-medium text-gray-700 dark:text-gray-300">
                {t("labels.loading")}...
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Ürün bilgileri getiriliyor
              </div>
            </div>
          ) : modalProducts.length > 0 ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center border dark:border-blue-800">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {modalProducts.length}
                  </div>
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    Toplam Ürün Çeşidi
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center border dark:border-green-800">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {modalProducts.reduce(
                      (sum, product) => sum + (product.product_quantity || 0),
                      0
                    )}
                  </div>
                  <div className="text-sm text-green-800 dark:text-green-300">
                    Toplam Ürün Adedi
                  </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center border dark:border-purple-800">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {Intl.NumberFormat("tr-TR", {
                      style: "currency",
                      currency: "TRY",
                    }).format(
                      modalProducts.reduce(
                        (sum, product) =>
                          sum +
                          (product.product_price || 0) *
                            (product.product_quantity || 0),
                        0
                      )
                    )}
                  </div>
                  <div className="text-sm text-purple-800 dark:text-purple-300">
                    Toplam Değer
                  </div>
                </div>
              </div>
              {/* Products Table */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    📦 Ürün Detay Listesi
                  </h3>
                </div>

                <Table
                  striped
                  highlightOnHover
                  className="mb-0"
                  classNames={{
                    table: "dark:!bg-gray-800",
                    thead: "dark:!bg-gray-700",
                    tbody: "dark:!bg-gray-800",
                    tr: "dark:!border-gray-700 dark:!bg-gray-800 hover:dark:!bg-gray-700",
                    th: "dark:!bg-gray-700 dark:!text-gray-100 dark:!border-gray-600",
                    td: "dark:!border-gray-700 dark:!bg-gray-800",
                  }}
                  styles={{
                    table: {
                      '&[data-mantine-color-scheme="dark"]': {
                        backgroundColor: "#1f2937 !important",
                      },
                    },
                    tr: {
                      '&[data-mantine-color-scheme="dark"]': {
                        backgroundColor: "#1f2937 !important",
                        borderColor: "#374151 !important",
                      },
                      '&[data-mantine-color-scheme="dark"]:hover': {
                        backgroundColor: "#374151 !important",
                      },
                    },
                    td: {
                      '&[data-mantine-color-scheme="dark"]': {
                        backgroundColor: "#1f2937 !important",
                        borderColor: "#374151 !important",
                      },
                    },
                    th: {
                      '&[data-mantine-color-scheme="dark"]': {
                        backgroundColor: "#374151 !important",
                        borderColor: "#4b5563 !important",
                        color: "#f9fafb !important",
                      },
                    },
                  }}
                >
                  <Table.Thead className="dark:bg-gray-700">
                    <Table.Tr>
                      <Table.Th className="text-center font-semibold text-gray-900 dark:text-gray-100">
                        Görsel
                      </Table.Th>
                      <Table.Th className="font-semibold text-gray-900 dark:text-gray-100">
                        Ürün Adı
                      </Table.Th>
                      <Table.Th className="text-center text-gray-900 dark:text-gray-100 font-semibold">
                        Marka
                      </Table.Th>
                      <Table.Th className="text-center text-gray-900 dark:text-gray-100 font-semibold">
                        Kategori
                      </Table.Th>
                      <Table.Th className="text-center text-gray-900 dark:text-gray-100 font-semibold">
                        Stok
                      </Table.Th>
                      <Table.Th className="text-center text-gray-900 dark:text-gray-100 font-semibold">
                        Satış Fiyatı
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody className="dark:hover:!bg-gray-600 dark:bg-gray-800">
                    {modalProducts.map((product, index) => (
                      <Table.Tr
                        key={`${product.product_id}-${index}`}
                        className="px-8 dark:!bg-gray-800 dark:!border-gray-700 hover:bg-gray-50 dark:hover:!bg-gray-700"
                      >
                        <Table.Td className="dark:!bg-gray-800 dark:!border-gray-700">
                          <div className="flex justify-center">
                            {product.product_image ? (
                              <img
                                src={`${config.CDN_URL}${product.product_image}`}
                                alt={product.product_name}
                                className="w-20 h-20 aspect-square object-cover rounded-xl shadow-md hover:shadow-lg transition-all duration-200 border bg-white border-gray-200 hover:scale-150"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className="w-20 h-20 aspect-square bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-xl flex items-center justify-center shadow-md border border-gray-200 dark:border-gray-600"
                              style={{
                                display: product.product_image
                                  ? "none"
                                  : "flex",
                              }}
                            >
                              <span className="text-gray-500 dark:text-gray-400 text-xs text-center font-medium">
                                📷
                                <br />
                                <span className="text-[10px]">Resim Yok</span>
                              </span>
                            </div>
                          </div>
                        </Table.Td>
                        <Table.Td className="dark:!bg-gray-800 dark:!border-gray-700">
                          <div className="font-medium text-gray-900 dark:text-gray-100 max-w-[140px]">
                            {product.product_name}
                          </div>
                        </Table.Td>
                        <Table.Td className="text-center dark:!bg-gray-800 dark:!border-gray-700">
                          <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300 rounded-full text-xs font-medium">
                            {product.product_brand || "-"}
                          </span>
                        </Table.Td>
                        <Table.Td className="text-center dark:!bg-gray-800 dark:!border-gray-700">
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium">
                            {product.product_category || "-"}
                          </span>
                        </Table.Td>
                        <Table.Td className="text-center dark:!bg-gray-800 dark:!border-gray-700">
                          <div className="font-bold text-blue-700 dark:text-blue-400 text-lg">
                            {product.product_quantity}
                          </div>
                        </Table.Td>
                        <Table.Td className="text-center dark:!bg-gray-800 dark:!border-gray-700">
                          <div className="font-bold text-green-700 dark:text-green-400 text-lg">
                            {Intl.NumberFormat("tr-TR", {
                              style: "currency",
                              currency: "TRY",
                            }).format(product.product_price || 0)}
                          </div>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {t("labels.no-products-found")}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SpotsCustomList;
